import { Box } from "@mui/material";
import { useTheme } from "@mui/material";
import { useEffect, useMemo } from "react";

import { EnhancedOption, HierarchicalOption } from "@/Components/v2/atoms/Autocomplete/types";
import { Stack } from "@/Components/v2/atoms/Stack";
import { LabelledAutoComplete } from "@/Components/v2/molecule/LabelledAutoComplete/LabelledAutoCompleteRefactor";
import LabelledComboBox from "@/Components/v2/molecule/LabelledAutoComplete/LabelledComboBox";
import LabelledHierarchyAutoComplete from "@/Components/v2/molecule/LabelledAutoComplete/LabelledHierarchyAutoComplete";
import { Option } from "@/Components/v2/molecule/LabelledAutoComplete/types";
import { NodeHierarchyAlert } from "@/Components/v2/organism/NodeHierarchyAlert/NodeHiearchyAlert";
import { Config } from "@/hooks/useConfig";
import { useInvestmentLevelsStore } from "@/pages/investment-levels/store/useInvestmentLevelsStore";
import {
  ComparisonMode,
  comparisonModesRequiringNodeUpdate,
} from "@/pages/investment-levels/types/comparisonMode";
import { NodeHierarchy, Nodes } from "@/pages/scenario-builder/utils/nodes";
import { Layer } from "@/Utils/hierarchy";
import { getMappedName } from "@/Utils/mapping-utils";

interface HeaderProps {
  config: Config;
  nodes: Nodes;
  hasGeography: boolean;
  selectedNodeUUID: string;
  channelOptions: string[];
  geographyOptions: string[];
  onTargetNodeChange: (targetNode: string | null) => void;
  onComparisonModeChange: (mode: ComparisonMode) => void;
  onChannelSelectionChange: (channels: string[]) => void;
  onGeoSelectionChange: (geography: string) => void;
}

export const Header: React.FC<HeaderProps> = ({
  config,
  nodes,
  hasGeography,
  selectedNodeUUID,
  channelOptions,
  geographyOptions,
  onComparisonModeChange,
  onTargetNodeChange,
  onChannelSelectionChange,
  onGeoSelectionChange,
}) => {
  const dropdownWidth = 250;
  const theme = useTheme();
  const availableLayers: Layer[] = nodes.getAvailableLayers().map((layer) => layer as Layer);

  const {
    comparisonMode,
    channels: selectedChannels,
    geography: selectedGeography,
  } = useInvestmentLevelsStore((state) => state.filters);

  const businessUnitHierarchyOptions = useMemo(
    () => createBusinessUnitHierarchyOptions(config, nodes, comparisonMode),
    [config, nodes, comparisonMode]
  );

  const mappedChannelOptions = useMemo(() => mapChannelOptions(channelOptions), [channelOptions]);
  const mappedGeoOptions = useMemo(
    () => mapGeographyOptions(geographyOptions, comparisonMode),
    [geographyOptions, comparisonMode]
  );

  useEffect(() => {
    if (hasGeography && mappedGeoOptions && mappedGeoOptions.length > 0 && !selectedGeography) {
      onGeoSelectionChange(mappedGeoOptions[0]?.value || "");
    }
  }, [hasGeography, mappedGeoOptions]);

  const comparisonModeOptions = [
    {
      mode: ComparisonMode.CHANNEL,
      label: "Channel Comparison",
      value: ComparisonMode.CHANNEL,
      available: true,
      selected: false,
    },
    {
      mode: ComparisonMode.GEOGRAPHY,
      label: "Geography Comparison",
      value: ComparisonMode.GEOGRAPHY,
      available: hasGeography,
      selected: false,
    },
    {
      mode: ComparisonMode.BRAND,
      label: `${config.getLayerLabel(Layer.Brand)} Comparison`,
      value: ComparisonMode.BRAND,
      available: true,
      selected: false,
    },
    {
      mode: ComparisonMode.CATEGORY,
      label: `${config.getLayerLabel(Layer.Category)} Comparison`,
      value: ComparisonMode.CATEGORY,
      available: availableLayers.includes(Layer.Category),
      selected: false,
    },
    {
      mode: ComparisonMode.PRODUCT,
      label: `${config.getLayerLabel(Layer.Product)} Comparison`,
      value: ComparisonMode.PRODUCT,
      available: availableLayers.includes(Layer.Product),
      selected: false,
    },
  ].filter((option) => option.available);

  const selectedComparisonModeOption =
    comparisonModeOptions.find((option) => option.value === comparisonMode) || null;

  const selectedChannelOptions = mappedChannelOptions.filter((option) =>
    selectedChannels.includes(option.value)
  );

  const selectedGeoOption =
    mappedGeoOptions.find((option) => option.value === selectedGeography) || null;

  const handleBusinessUnitSelectionChange = (selected: EnhancedOption) => {
    onTargetNodeChange(selected.value);
  };

  const handleComparisonModeChange = (selected: Option) => {
    onComparisonModeChange(selected.value as ComparisonMode);
    onChannelSelectionChange([]);
    onGeoSelectionChange("");
  };

  const handleChannelSelectionChange = (selected: Option[]) => {
    onChannelSelectionChange(selected.map((option) => option.value));
  };

  const handleGeoSelectionChange = (selected: Option) => {
    onGeoSelectionChange(selected.value);
  };

  const getRootLevelLabel = () => {
    switch (comparisonMode) {
      case ComparisonMode.BRAND:
        return `All ${config!.getLayerLabel(Layer.Brand)}`;
      case ComparisonMode.CATEGORY:
        return `All ${config!.getLayerLabel(Layer.Category)}`;
      case ComparisonMode.PRODUCT:
        return `All ${config!.getLayerLabel(Layer.Product)}`;
      default:
        return null;
    }
  };

  return (
    <Stack
      direction="column"
      spacing={2}
      margin={4}
      marginY={2}
      padding={3}
      border={`1px solid ${theme.palette.section.border}`}
      borderRadius={4}
    >
      <Box
        sx={{
          display: "flex",
          gap: 2,
          justifyContent: "flex-start",
          flexWrap: "wrap",
          "& > *": {
            width: "280px",
            maxWidth: "280px",
          },
        }}
      >
        <LabelledComboBox
          label="Step 1: Select View"
          id="comparison-mode"
          options={comparisonModeOptions}
          selectedOption={selectedComparisonModeOption}
          onSelectChange={handleComparisonModeChange}
          width={dropdownWidth}
        />
        <LabelledHierarchyAutoComplete
          label="Step 2: Select Business Unit"
          id="business-unit"
          disableClearable
          options={businessUnitHierarchyOptions}
          onSelectChange={handleBusinessUnitSelectionChange}
          value={selectedNodeUUID}
          width={dropdownWidth}
          disabled={comparisonModesRequiringNodeUpdate.includes(comparisonMode)}
          key={`hierarchy-autocomplete-${comparisonMode}`}
        />
        {(() => {
          switch (comparisonMode) {
            case ComparisonMode.CHANNEL:
              return (
                <LabelledAutoComplete
                  multiple={true}
                  label="Step 3: Select Channel"
                  id="channels"
                  options={mappedChannelOptions}
                  selectedOptions={selectedChannelOptions}
                  onSelectChange={handleChannelSelectionChange}
                  allOptionsLabel="Select All Channels"
                  width={dropdownWidth}
                />
              );
            default:
              return (
                <LabelledAutoComplete
                  multiple={false}
                  label="Step 3: Select Channel"
                  id="channels"
                  options={mappedChannelOptions}
                  selectedOptions={selectedChannelOptions}
                  onSelectChange={handleChannelSelectionChange}
                  allOptionsLabel="Select All Channels"
                  width={dropdownWidth}
                />
              );
          }
        })()}
        {hasGeography && (
          <LabelledComboBox
            label="Step 4: Select Geography"
            id="geographies"
            options={mappedGeoOptions}
            selectedOption={selectedGeoOption}
            onSelectChange={handleGeoSelectionChange}
            disabled={comparisonMode === ComparisonMode.GEOGRAPHY}
            width={dropdownWidth}
          />
        )}
      </Box>

      <NodeHierarchyAlert nodeUUID={selectedNodeUUID} rootLevelLabel={getRootLevelLabel()} />
    </Stack>
  );
};

export default Header;

const createBusinessUnitHierarchyOptions = (
  config: Config,
  nodes: Nodes,
  comparisonMode: ComparisonMode
): HierarchicalOption[] => {
  const rootNodeId = 0;

  // TODO: Find a better way to handle showing label
  const getLabelForComparisonMode = (node: NodeHierarchy, mode: ComparisonMode): string => {
    switch (mode) {
      case ComparisonMode.BRAND:
        return `All ${config!.getLayerLabel(Layer.Brand)}`;
      case ComparisonMode.CATEGORY:
        return `All ${config!.getLayerLabel(Layer.Category)}`;
      case ComparisonMode.PRODUCT:
        return `All ${config!.getLayerLabel(Layer.Product)}`;
      default:
        return node.label;
    }
  };

  const mapNodeToOption = (node: NodeHierarchy): HierarchicalOption => {
    const label = getLabelForComparisonMode(node, comparisonMode);
    return {
      id: node.id,
      label: label,
      value: node.uuid,
      children: node.children.map(mapNodeToOption),
      alwaysOpen: node.id === rootNodeId,
    };
  };

  const hierarchicalOptions = nodes.getHierarchy({ shouldHidePortfolio: true });
  return hierarchicalOptions.map(mapNodeToOption);
};

const mapChannelOptions = (channelOptions: string[]): Option[] => {
  return channelOptions
    .map((channel) => ({
      label: getMappedName(channel) ?? channel,
      value: channel,
      selected: false,
    }))
    .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: "base" }));
};

const mapGeographyOptions = (
  geographyOptions: string[],
  comparisonMode: ComparisonMode
): Option[] => {
  const getLabelForComparisonMode = (geography: string, mode: ComparisonMode): string => {
    if (geography === "national") {
      return `All Geos`;
    }
    switch (mode) {
      case ComparisonMode.GEOGRAPHY:
        return `All Geographies`;
      default:
        return getMappedName(geography) ?? geography;
    }
  };

  const options = geographyOptions
    .map(
      (geography) =>
        ({
          label: getLabelForComparisonMode(geography, comparisonMode),
          value: geography,
          selected: false,
        }) as Option
    )
    .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: "base" }));

  // Find National and move it to the top if it exists
  const nationalIndex = options.findIndex((option) => option.value.toLowerCase() === "national");

  if (nationalIndex > -1) {
    const [national] = options.splice(nationalIndex, 1) as [Option];
    options.unshift(national);
  }

  return options;
};
